// Standardise / Add-to-menu dialog (spec §12): publishes EXISTING objects to
// the app's shared navigation via the nav_edit-backed publish-to-navigation
// endpoint (idempotent, no regeneration). Two voices, one flow:
//   mode "standardise" — visuals moving to the shared menu (default folder
//   "Standardized", the reserved folder approvals also use)
//   mode "menu"        — any object (e.g. a dashboard) being wired into the
//   menu under a chosen folder (or at the top level)
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Check } from 'lucide-react'
import Modal from '@/components/common/Modal'
import { publishToNavigation } from '@/api/designers'
import { getJsonRaw } from '@/api/generateapp'
import { TypeChip, errMessage } from './shared'

export interface PublishTarget {
  obj_name: string
  obj_type: string
  title: string
}

const STANDARDIZED_FOLDER = 'Standardized'
const TOP_LEVEL = '__top__'

export default function PublishToMenuDialog({
  open, mode, project, targets, onClose, onPublished,
}: {
  open: boolean
  mode: 'standardise' | 'menu'
  project: string
  targets: PublishTarget[]
  onClose: () => void
  /** Called after at least one object was published (refresh catalogs). */
  onPublished: () => void
}) {
  const [folders, setFolders] = useState<string[]>([])
  const [folder, setFolder] = useState<string>(
    mode === 'standardise' ? STANDARDIZED_FOLDER : TOP_LEVEL)
  const [customFolder, setCustomFolder] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  // Existing top-level menu folders for the folder select (flat raw nav:
  // obj_type '-' at the root). Fail-soft — the defaults always work.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    ;(async () => {
      try {
        const data = await getJsonRaw(project) as {
          navigation?: Array<{ title?: string; obj_type?: string; parent_id?: string }>
        }
        if (cancelled) return
        const names = (data.navigation || [])
          .filter((n) => (n.obj_type || '-') === '-' && !(n.parent_id || '').trim())
          .map((n) => (n.title || '').trim())
          .filter(Boolean)
        setFolders(Array.from(new Set(names)))
      } catch {
        if (!cancelled) setFolders([])
      }
    })()
    return () => { cancelled = true }
  }, [open, project])

  useEffect(() => {
    if (open) {
      setFolder(mode === 'standardise' ? STANDARDIZED_FOLDER : TOP_LEVEL)
      setCustomFolder('')
      setNote('')
    }
  }, [open, mode])

  const folderOptions = useMemo(() => {
    const opts: { value: string; label: string }[] = []
    if (mode === 'menu') opts.push({ value: TOP_LEVEL, label: 'Top level (no folder)' })
    opts.push({ value: STANDARDIZED_FOLDER, label: STANDARDIZED_FOLDER })
    for (const f of folders) {
      if (f !== STANDARDIZED_FOLDER) opts.push({ value: f, label: f })
    }
    opts.push({ value: '__custom__', label: 'New folder…' })
    return opts
  }, [mode, folders])

  const resolvedFolder =
    folder === TOP_LEVEL ? '' : folder === '__custom__' ? customFolder.trim() : folder

  const submit = async () => {
    if (targets.length === 0) {
      toast('Select at least one visual first')
      return
    }
    if (folder === '__custom__' && !customFolder.trim()) {
      toast.error('Name the new folder first')
      return
    }
    setBusy(true)
    let done = 0
    const failures: string[] = []
    for (const t of targets) {
      try {
        await publishToNavigation(project, {
          obj_name: t.obj_name,
          obj_type: t.obj_type,
          title: t.title,
          folder_title: resolvedFolder || undefined,
          description: note.trim() || undefined,
        })
        done += 1
      } catch (err) {
        failures.push(`${t.title}: ${errMessage(err, 'failed')}`)
      }
    }
    setBusy(false)
    if (done > 0) {
      toast.success(
        mode === 'standardise'
          ? `${done} visual${done > 1 ? 's' : ''} standardised — now on the shared menu`
          : `Added to the app menu${resolvedFolder ? ` under "${resolvedFolder}"` : ''}`)
      onPublished()
    }
    if (failures.length > 0) {
      toast.error(`${failures.length} failed — ${failures[0]}`)
    }
    if (done > 0 && failures.length === 0) onClose()
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px', fontSize: 15,
    border: '1px solid var(--color-border-strong)', borderRadius: 7,
    background: 'var(--color-input-bg)', color: 'var(--color-text)',
  }

  return (
    <Modal
      open={open}
      onClose={() => { if (!busy) onClose() }}
      title={mode === 'standardise' ? 'Standardise visuals' : 'Add to the app menu'}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={() => void submit()}
            disabled={busy || targets.length === 0}
          >
            <Check size={14} />
            {busy
              ? 'Publishing…'
              : mode === 'standardise' ? 'Standardise' : 'Add to menu'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 14.5, color: 'var(--color-text-secondary)' }}>
          {mode === 'standardise'
            ? 'Standardised visuals move to the shared menu for everyone, with you shown as the author.'
            : 'The object is wired into the application menu instantly — nothing is regenerated.'}
        </p>

        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 5 }}>Selected</div>
          <div style={{ display: 'grid', gap: 5 }}>
            {targets.map((t) => (
              <span
                key={t.obj_name}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: 'var(--color-bg-secondary)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 7, padding: '5px 9px', fontSize: 14.5,
                }}
              >
                <TypeChip code={t.obj_type} size={16} />
                {t.title}
              </span>
            ))}
            {targets.length === 0 && (
              <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
                Nothing selected.
              </span>
            )}
          </div>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            Menu folder
          </label>
          <select
            style={inputStyle}
            value={folder}
            onChange={(e) => setFolder(e.target.value)}
          >
            {folderOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {folder === '__custom__' && (
            <input
              style={{ ...inputStyle, marginTop: 6 }}
              value={customFolder}
              autoFocus
              placeholder="Folder name, e.g. Executive"
              onChange={(e) => setCustomFolder(e.target.value)}
            />
          )}
        </div>

        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            Note <span style={{ fontWeight: 400, color: 'var(--color-text-secondary)' }}>(optional)</span>
          </label>
          <textarea
            style={{ ...inputStyle, resize: 'vertical' }}
            rows={2}
            value={note}
            placeholder={mode === 'standardise'
              ? 'Why these should be shared…'
              : 'Shown as the menu entry’s description…'}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  )
}
